/**
 * useHeroPerf · single source of truth for hero animation budget.
 *
 * The landing hero has multiple expensive layers stacked together:
 *   · ambient map video (8.6 MB)
 *   · pure-anime.js dot grid + cursor ripple (153 dots, recurring stagger)
 *   · SVG route + motion-path truck loop
 *   · physics-style draggable orbs with spring release
 *   · react-three-fiber wireframe scene (three.js, WebGL)
 *   · scroll-linked parallax on the foreground copy
 *
 * On a high-end desktop this is fine. On a mid-range phone, on a tab in
 * the background, or on a user with `prefers-reduced-motion`, all of it
 * together can pin the main thread, drain battery, and feel laggy.
 *
 * This hook exposes ONE object that every hero animation reads to decide
 * what to render and how hard to push. Components do not each re-derive
 * the same media-query / connection / hardware checks.
 *
 * Tiers
 * -----
 *   · `reduced`       · user prefers reduced motion · skip everything,
 *                       snap to final state
 *   · `lowPower`      · mobile, low-end CPU, low memory, save-data, slow
 *                       network · keep static layers, skip canvases /
 *                       three.js / cursor ripple / continuous loops
 *   · `medium`        · tablets, mid-range laptops · keep most layers
 *                       but at lower fidelity (no three.js scene, no
 *                       continuous breathing on the dot grid)
 *   · `high`          · desktops with a real GPU · everything on
 *
 * Plus boolean gates for individual layers so each component can ask one
 * question without thinking about tiers.
 */

import { useEffect, useState } from "react";

export type HeroPerfTier = "reduced" | "lowPower" | "medium" | "high";

export interface HeroPerfBudget {
  tier: HeroPerfTier;
  /** OS-level reduced-motion request · trumps everything. */
  reduced: boolean;
  /** Tab is hidden · skip continuous loops to save battery. */
  hidden: boolean;
  // Per-layer gates · components read these directly.
  allowAmbientVideo: boolean;
  allowDotGrid: boolean;
  allowCursorRipple: boolean;
  allowBreathingLoop: boolean;
  allowRouteLoop: boolean;
  allowDraggableOrbs: boolean;
  allowWireframeScene: boolean;
  allowScrollParallax: boolean;
  /** Suggested DPR ceiling for any <Canvas> · keeps GPU cost bounded. */
  maxDpr: number;
}

interface NavigatorWithExtras extends Navigator {
  deviceMemory?: number;
  connection?: {
    saveData?: boolean;
    effectiveType?: string;
  };
}

function detectTier(): { tier: HeroPerfTier; reduced: boolean } {
  if (typeof window === "undefined" || !window.matchMedia) {
    return { tier: "high", reduced: false };
  }

  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduced) return { tier: "reduced", reduced: true };

  const nav = navigator as NavigatorWithExtras;
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const isCoarse = window.matchMedia("(pointer: coarse)").matches;
  const memory = typeof nav.deviceMemory === "number" ? nav.deviceMemory : null;
  const cores = typeof nav.hardwareConcurrency === "number" ? nav.hardwareConcurrency : null;
  const conn = nav.connection ?? null;
  const saveData = conn?.saveData === true;
  const slowNet =
    conn?.effectiveType === "slow-2g" ||
    conn?.effectiveType === "2g" ||
    conn?.effectiveType === "3g";
  const lowDpr = (window.devicePixelRatio ?? 1) <= 1;
  const lowMem = memory !== null && memory <= 4;
  const lowCpu = cores !== null && cores <= 4;

  // Hard low-power signals · phones, save-data, slow net, weak hardware.
  if (isMobile || isCoarse || saveData || slowNet || lowMem) {
    return { tier: "lowPower", reduced: false };
  }
  // Soft signals · tablet-class or modest laptop.
  if (lowCpu || lowDpr) {
    return { tier: "medium", reduced: false };
  }
  return { tier: "high", reduced: false };
}

function budgetFor(tier: HeroPerfTier, reduced: boolean, hidden: boolean): HeroPerfBudget {
  const base: HeroPerfBudget = {
    tier,
    reduced,
    hidden,
    allowAmbientVideo: false,
    allowDotGrid: false,
    allowCursorRipple: false,
    allowBreathingLoop: false,
    allowRouteLoop: false,
    allowDraggableOrbs: false,
    allowWireframeScene: false,
    allowScrollParallax: false,
    maxDpr: 1,
  };

  if (reduced) return base;

  // When the tab is hidden, we still allow static layers but kill all
  // continuous loops · the visibility listener pauses things; this gate
  // ensures we never start a fresh loop while hidden either.
  const allowLoops = !hidden;

  if (tier === "lowPower") {
    return {
      ...base,
      allowDotGrid: false,
      allowCursorRipple: false,
      allowBreathingLoop: false,
      allowRouteLoop: false,
      allowDraggableOrbs: false,
      allowWireframeScene: false,
      allowScrollParallax: allowLoops, // cheap, transform-only
      maxDpr: 1,
    };
  }

  if (tier === "medium") {
    return {
      ...base,
      allowAmbientVideo: allowLoops,
      allowDotGrid: true,
      allowCursorRipple: true,
      allowBreathingLoop: false, // skip the always-on stagger wave
      allowRouteLoop: allowLoops,
      allowDraggableOrbs: true,
      allowWireframeScene: false, // skip three.js on tablets/modest laptops
      allowScrollParallax: true,
      maxDpr: 1.25,
    };
  }

  // high
  return {
    ...base,
    allowAmbientVideo: allowLoops,
    allowDotGrid: true,
    allowCursorRipple: true,
    allowBreathingLoop: allowLoops,
    allowRouteLoop: allowLoops,
    allowDraggableOrbs: true,
    allowWireframeScene: true,
    allowScrollParallax: true,
    maxDpr: 1.75,
  };
}

/**
 * Hook · returns the live hero perf budget. Re-renders when the user
 * toggles reduced motion, switches tabs, or (rare) the network changes.
 */
export function useHeroPerf(): HeroPerfBudget {
  const [{ tier, reduced }, setTier] = useState(detectTier);
  const [hidden, setHidden] = useState<boolean>(() =>
    typeof document !== "undefined" ? document.hidden : false,
  );

  // Re-evaluate tier on motion-pref / connection changes.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setTier(detectTier());
    mql.addEventListener?.("change", onChange);
    const conn = (navigator as NavigatorWithExtras).connection;
    conn && "addEventListener" in conn
      ? (conn as unknown as EventTarget).addEventListener?.("change", onChange)
      : undefined;
    return () => {
      mql.removeEventListener?.("change", onChange);
      conn && "removeEventListener" in conn
        ? (conn as unknown as EventTarget).removeEventListener?.("change", onChange)
        : undefined;
    };
  }, []);

  // Track tab visibility · used by per-layer gates to skip loops.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const onVis = () => setHidden(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return budgetFor(tier, reduced, hidden);
}

/** Synchronous one-shot read · for non-React modules / lazy imports. */
export function readHeroPerf(): HeroPerfBudget {
  const { tier, reduced } = detectTier();
  const hidden = typeof document !== "undefined" ? document.hidden : false;
  return budgetFor(tier, reduced, hidden);
}
